import React from 'react';
import { Button, InputNumber, Space, Typography, Tooltip } from 'antd';
import {
  PlusOutlined,
  CloseOutlined,
  BulbOutlined,
  CheckCircleFilled,
  WarningFilled,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import ShelfAutocomplete from './ShelfAutocomplete';
import { formatBoxPreview } from './boxPreview';
import { useReceiptPutaway } from '@contexts/operator/ReceiptPutawayContext';

const { Text } = Typography;

function AllocationStatus({ allocated, total }) {
  const { t } = useTranslation();
  const remaining = total - allocated;

  if (allocated === 0) {
    return (
      <Text type="secondary" style={{ fontSize: 12 }}>
        {t('operator.pages.putaway.allocation.unallocated', {
          total,
          defaultValue: '尚未分配（共 {{total}} 件）',
        })}
      </Text>
    );
  }

  if (remaining === 0) {
    return (
      <Space size={6} style={{ fontSize: 12 }}>
        <CheckCircleFilled style={{ color: '#52c41a' }} />
        <Text style={{ color: '#52c41a', fontSize: 12, fontWeight: 500 }}>
          {t('operator.pages.putaway.allocation.complete', {
            total,
            defaultValue: '分配完成（{{total}} 件）',
          })}
        </Text>
      </Space>
    );
  }

  if (remaining < 0) {
    return (
      <Space size={6} style={{ fontSize: 12 }}>
        <WarningFilled style={{ color: '#ff4d4f' }} />
        <Text type="danger" style={{ fontSize: 12, fontWeight: 500 }}>
          {t('operator.pages.putaway.allocation.overflow', {
            allocated,
            total,
            overflow: -remaining,
            defaultValue: '超出 {{overflow}} 件（已配 {{allocated}} / 上限 {{total}}）',
          })}
        </Text>
      </Space>
    );
  }

  return (
    <Text style={{ fontSize: 12, color: '#1677ff' }}>
      {t('operator.pages.putaway.allocation.in_progress', {
        allocated,
        total,
        remaining,
        defaultValue: '已配 {{allocated}} / {{total}}，還需 {{remaining}} 件',
      })}
    </Text>
  );
}

export default function ShelfAllocations({ itemId, remaining, warehouseId, quantityPerBox, onRecommend }) {
  const { t } = useTranslation();
  const { state, actions } = useReceiptPutaway();
  const rows = state.rowStates[itemId] || [];

  // Only rows with a resolved shelf count as allocated.
  const allocated = rows.reduce((s, r) => {
    if (!r.shelfId) return s;
    return s + Number(r.quantity || 0);
  }, 0);

  const handleAdd = () => {
    const lastQty = Number(rows[rows.length - 1]?.quantity || 0);
    actions.addShelfRow(itemId, lastQty);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
      {rows.map((r, idx) => {
        const isFirst = idx === 0;
        const isLast = idx === rows.length - 1;
        const canRemove = rows.length > 1;
        return (
          <div key={r.key} style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%' }}>
            {isFirst ? (
              <Tooltip title={t('operator.pages.putaway.actions.view_recommendations')}>
                <Button
                  size="small"
                  type="text"
                  icon={<BulbOutlined />}
                  onClick={() => onRecommend?.(itemId)}
                  style={{ color: '#faad14', flex: '0 0 auto' }}
                />
              </Tooltip>
            ) : (
              <span style={{ flex: '0 0 24px' }} />
            )}
            <ShelfAutocomplete
              value={r.location}
              warehouseId={warehouseId}
              onChange={({ shelfId, location }) =>
                actions.updateShelfRow(itemId, r.key, { shelfId, location })
              }
              style={{ flex: '1 1 auto', minWidth: 0, maxWidth: 220 }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flex: '0 0 88px' }}>
              <InputNumber
                min={0}
                max={remaining}
                value={r.quantity}
                onChange={(v) => actions.updateShelfRow(itemId, r.key, { quantity: v ?? 0 })}
                style={{ width: 88 }}
                size="middle"
                controls={false}
              />
              {(() => {
                const preview = formatBoxPreview(r.quantity, quantityPerBox, t);
                if (!preview) return null;
                return (
                  <Text type="secondary" style={{ fontSize: 11, lineHeight: '14px', marginTop: 2 }}>
                    {preview}
                  </Text>
                );
              })()}
            </div>
            {canRemove ? (
              <Button
                size="small"
                type="text"
                icon={<CloseOutlined />}
                onClick={() => actions.removeShelfRow(itemId, r.key)}
                title={t('operator.pages.putaway.actions.remove_shelf')}
                style={{ flex: '0 0 auto' }}
              />
            ) : (
              <span style={{ flex: '0 0 24px' }} />
            )}
            {isLast ? (
              <Button
                size="small"
                type="dashed"
                icon={<PlusOutlined />}
                onClick={handleAdd}
                title={t('operator.pages.putaway.actions.add_shelf')}
                style={{ flex: '0 0 auto' }}
              />
            ) : (
              <span style={{ flex: '0 0 24px' }} />
            )}
          </div>
        );
      })}

      <div style={{ paddingLeft: 30 }}>
        <AllocationStatus allocated={allocated} total={remaining} />
      </div>
    </div>
  );
}
